import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaQuestionCircle,
  FaGraduationCap,
  FaBriefcase,
  FaClock,
  FaLaptop,
  FaDollarSign,
  FaBook,
  FaCheckCircle,
  FaSearch,
  FaStar,
  FaUsers,
  FaChalkboardTeacher,
  FaRegClock,
  FaGlobe,
  FaPhone,
  FaEnvelope,
  FaCertificate,
  FaAward,
  FaLaptopHouse,
  FaUserGraduate,
  FaChartLine,
  FaBuilding,
  FaHome,
  FaChalkboard,
  FaBolt,
  FaStethoscope,
  FaIdCard,
  FaClipboardCheck,
  FaCalendarWeek,
  FaFileSignature,
  FaChevronDown,
  FaChevronRight
} from "react-icons/fa";

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 140px);
  background: #ffffff;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin-top: 155px;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    margin-top: 145px;
  }


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 50%, #1e1b4b 100%);
  color: white;
  padding: 3.5rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 2.8rem 5%;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 2.2rem 5%;
  }

  @media (max-width: 768px) {
    padding: 3rem 4%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 0.8rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffff;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.4rem;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.8rem;
    margin-bottom: 0.6rem;
    span { font-size: 1.8rem; }
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    span { font-size: 1.6rem; }
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    span { font-size: 1.3rem; }
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 1.8rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.15rem;
    max-width: 650px;
    margin: 0 auto 1.5rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 1.1rem;
    max-width: 600px;
    margin: 0 auto 1.2rem;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0 auto 1.2rem;
  }
`;

const SearchContainer = styled.div`
  max-width: 550px;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  @media (max-width: 1366px) and (min-width: 769px) {
    max-width: 520px;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    max-width: 480px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.9rem 1.3rem 0.9rem 3.2rem;
  border: 2px solid transparent;
  border-radius: 50px;
  font-size: 0.95rem;
  outline: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  font-family: 'Times New Roman', serif;
  
  &:focus {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
    border-color: #ffd966;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.85rem 1.2rem 0.85rem 3rem;
    font-size: 0.95rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0.8rem 1rem 0.8rem 2.8rem;
    font-size: 0.9rem;
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 1.3rem;
  top: 50%;
  transform: translateY(-50%);
  color: #3730A3;
  font-size: 1.1rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    left: 1.2rem;
    font-size: 1.05rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    left: 1rem;
    font-size: 1rem;
  }
`;

const CategoriesContainer = styled.div`
  max-width: 1200px;
  margin: -1.8rem auto 2.5rem;
  padding: 0 5%;
  position: relative;
  z-index: 10;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin: -1.5rem auto 2.2rem;
    padding: 0 4%;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    margin: -1.2rem auto 2rem;
    padding: 0 3%;
  }
`;

const CategoriesGrid = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.2rem;
  box-shadow: 0 10px 35px rgba(55, 48, 163, 0.08);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 0.6rem;
  border: 1px solid rgba(55, 48, 163, 0.06);

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.1rem;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.5rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 0.4rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    padding: 1rem;
  }
`;

const CategoryButton = styled.button`
  padding: 0.7rem;
  border: none;
  border-radius: 12px;
  background: ${props => props.active ? props.color : '#f8f7fc'};
  color: ${props => props.active ? 'white' : '#4a4a6a'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  font-family: 'Times New Roman', serif;
  
  &:hover {
    background: ${props => props.active ? props.color : '#f0efff'};
    transform: translateY(-2px);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.65rem 0.5rem;
    gap: 0.3rem;
    border-radius: 10px;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0.6rem 0.4rem;
    gap: 0.25rem;
    border-radius: 10px;
  }
`;

const CategoryIcon = styled.div`
  font-size: 1.3rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.25rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 1.2rem;
  }
`;

const CategoryName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.78rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 0.75rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 900px;
  margin: 0 auto 3.5rem;
  padding: 0 5%;

  @media (max-width: 1366px) and (min-width: 769px) {
    max-width: 850px;
    margin: 0 auto 3rem;
    padding: 0 4%;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    max-width: 800px;
    margin: 0 auto 2.5rem;
    padding: 0 3%;
  }

  @media (max-width: 768px) {
    padding: 0 4%;
  }
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 16px;
  margin-bottom: 0.7rem;
  box-shadow: 0 4px 15px rgba(55, 48, 163, 0.04);
  border: 1px solid rgba(55, 48, 163, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(55, 48, 163, 0.08);
    border-color: rgba(55, 48, 163, 0.15);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    border-radius: 14px;
    margin-bottom: 0.65rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    border-radius: 12px;
    margin-bottom: 0.6rem;
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.2rem 1.3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  font-family: 'Times New Roman', serif;
  
  &:hover {
    background: rgba(55, 48, 163, 0.02);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1.1rem 1.2rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 1rem 1.1rem;
  }
`;

const QuestionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex: 1;

  @media (max-width: 1366px) and (min-width: 769px) {
    gap: 0.8rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    gap: 0.7rem;
  }
`;

const QuestionIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.bg || '#f8f7fc'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.iconColor || '#3730A3'};
  font-size: 1.1rem;
  flex-shrink: 0;

  @media (max-width: 1366px) and (min-width: 769px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    border-radius: 10px;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    width: 38px;
    height: 38px;
    font-size: 0.95rem;
    border-radius: 10px;
  }
`;

const QuestionText = styled.h3`
  font-size: 1.05rem;
  color: #1a1a2e;
  font-weight: 600;
  margin: 0;
  flex: 1;
  line-height: 1.4;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 0.95rem;
  }
`;

const ChevronIcon = styled.div`
  color: #3730A3;
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.3s ease;
  flex-shrink: 0;
  font-size: 0.9rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 0.8rem;
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 1.3rem 1.2rem 4.8rem;
  color: #4a4a6a;
  line-height: 1.7;
  font-size: 0.95rem;
  font-family: 'Times New Roman', serif;
  
  p {
    margin-bottom: 0.8rem;
  }
  
  ul, ol {
    margin: 0.4rem 0 0.8rem 1.5rem;
  }
  
  li {
    margin-bottom: 0.2rem;
  }
  
  strong {
    color: #3730A3;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0 1.2rem 1rem 4.4rem;
    font-size: 0.92rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0 1rem 0.9rem 4rem;
    font-size: 0.9rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: #4a4a6a;
  
  svg {
    font-size: 3.5rem;
    color: #d0d0e0;
    margin-bottom: 0.8rem;
  }
  
  h3 {
    color: #1a1a2e;
    margin-bottom: 0.5rem;
    font-family: 'Times New Roman', serif;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 2.5rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 2rem;
  }
`;

const StillHaveQuestions = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 100%);
  color: white;
  padding: 3.5rem 5%;
  text-align: center;

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 3rem 5%;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 2.5rem 5%;
  }

  @media (max-width: 768px) {
    padding: 3rem 5%;
  }
`;

const StillTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 0.8rem;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.3rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 2.1rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StillDescription = styled.p`
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto 1.8rem;
  opacity: 0.9;
  font-family: 'Times New Roman', serif;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 1.05rem;
    margin: 0 auto 1.5rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    font-size: 1rem;
    margin: 0 auto 1.2rem;
  }
`;

const ContactButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 1.8rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-family: 'Times New Roman', serif;
  font-size: 1rem;
  
  &.primary {
    background: #ffd966;
    color: #3730A3;
    
    &:hover {
      background: white;
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    }
  }
  
  &.secondary {
    background: transparent;
    color: white;
    border: 2px solid white;
    
    &:hover {
      background: white;
      color: #3730A3;
      transform: translateY(-2px);
    }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.85rem 1.6rem;
    font-size: 0.95rem;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0.8rem 1.4rem;
    font-size: 0.9rem;
  }
`;

const FAQ = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const categories = [
    { id: "all", name: "All Questions", icon: <FaQuestionCircle />, color: "#3730A3" },
    { id: "basics", name: "Basics of Medical Coding", icon: <FaGraduationCap />, color: "#0d9488" },
    { id: "eligibility", name: "Eligibility & Qualifications", icon: <FaUsers />, color: "#7c3aed" },
    { id: "training", name: "Training & Courses", icon: <FaChalkboardTeacher />, color: "#d97706" },
    { id: "career", name: "Career Opportunities", icon: <FaBriefcase />, color: "#dc2626" },
    { id: "certification", name: "Certification", icon: <FaCertificate />, color: "#2563eb" },
    { id: "logistics", name: "Logistics & Scheduling", icon: <FaClock />, color: "#059669" }
  ];

  const faqs = [
    {
      id: 1,
      category: "basics",
      question: "What is Medical Billing and Coding?",
      answer: `
        <p><strong>Medical Billing and Coding</strong> are two interrelated processes. As part of the medical coding process, one would need to read, understand and convert written documentation into alphanumeric codes. These codes are then submitted by medical billers to the insurance companies for reimbursement purposes.</p>
        <p>Processing patient information, diagnoses, and procedures/services performed during the treatment phase requires proper training and skill development to accomplish the tasks accurately.</p>
      `,
      icon: <FaGraduationCap />,
      iconColor: "#0d9488"
    },
    {
      id: 44,
      category: "basics",
      question: "What will I learn?",
      answer: `
        <p>Medical coding systems differ with appropriate settings for whom do we work: For example, physician practice or hospital practice. The curriculum differs with each practice. By and large, all our modules will teach the basics of the coding systems such as ICD-10-CM, ICD-10-PCS, CPT, and HCPCS.</p>
      `,
      icon: <FaBook />,
      iconColor: "#0d9488"
    },
    {
      id: 45,
      category: "basics",
      question: "What is the difference between AHIMA and AAPC?",
      answer: `
        <p>AHIMA and AAPC are two organizations which provide certification for medical coders to prove their skill-set in medical coding.</p>
        <p><strong>AHIMA</strong> focuses on certifications mainly in the area of inpatient coding, medical record management, and healthcare administration apart from outpatient coding certifications.</p>
        <p><strong>AAPC</strong> focuses on certifications mainly in the outpatient setting or physician office settings. However, they are expanding to other settings as well.</p>
      `,
      icon: <FaAward />,
      iconColor: "#0d9488"
    },
    {
      id: 46,
      category: "basics",
      question: "Do you teach medical terminology as well?",
      answer: `
        <p>Yes, we do teach medical terminology that is required to learn medical coding. However, every student is expected to have a basic knowledge of terminology prior to enrolling into a course.</p>
      `,
      icon: <FaStethoscope />,
      iconColor: "#0d9488"
    },
    {
      id: 2,
      category: "eligibility",
      question: "What are the Eligibility/Preferred Qualifications to Become a Medical Biller/Medical Coder?",
      answer: `
        <p><strong>A minimum of baccalaureate degree is a must for all medical coding and billing professionals.</strong> Life science graduates are preferred and have an edge over non-life science graduates.</p>
        <p><strong>Preferred Qualifications:</strong></p>
        <ul>
          <li>Graduate or postgraduate in life sciences: BSc, BTech (Biotechnology)</li>
          <li>BPharm, Nursing professionals</li>
          <li>MBBS, DNM, BDS</li>
          <li>Doctor of alternative medicine: Naturopathy, Ayurveda, Siddha, Homeopathy etc.</li>
        </ul>
        <p><strong>Note:</strong> Candidates who graduated in non-life science background may also aspire for a medical coding career provided they have strong knowledge in medical terminology, anatomy and pathophysiology.</p>
      `,
      icon: <FaUsers />,
      iconColor: "#7c3aed"
    },
    {
      id: 3,
      category: "eligibility",
      question: "I Have Not Completed My Graduation, Can I Learn Medical Coding?",
      answer: `
        <p><strong>Yes, you can learn medical coding</strong>, but the chances of employability are less if you have not completed your graduation.</p>
        <p>While training is open to everyone, most employers require a minimum of a bachelor's degree for medical coding positions.</p>
      `,
      icon: <FaGraduationCap />,
      iconColor: "#7c3aed"
    },
    {
      id: 4,
      category: "eligibility",
      question: "Can I Get A Job In RCM Industry Without A Graduation?",
      answer: `
        <p><strong>It is not advisable to pursue medical coding training without a graduation.</strong></p>
        <p>The RCM industry typically requires at least a bachelor's degree for employment. While you can learn the skills, job opportunities will be significantly limited without a degree.</p>
      `,
      icon: <FaBriefcase />,
      iconColor: "#7c3aed"
    },
    {
      id: 37,
      category: "eligibility",
      question: "Do I need to take a pretest before getting into training?",
      answer: `
        <p>No. You do not need to take any pretest to enter any of the training courses. However, we recommend every candidate to understand the basic requirements of the industry and prepare for the training accordingly.</p>
      `,
      icon: <FaClipboardCheck />,
      iconColor: "#7c3aed"
    },
    {
      id: 5,
      category: "training",
      question: "Why Should I Choose Learn Thru Us Training Academy?",
      answer: `
        <div style="display: flex; gap: 1rem; margin-bottom: 1rem;">
          <div style="flex-shrink: 0; width: 36px; height: 36px; background: #f5f3ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #7c3aed; font-size: 1rem;">★</div>
          <div>
            <p><strong>Unique Technique:</strong> A proven four-step training methodology called <strong>"Rely Methodology"</strong> – Explain, Show, Do and Review. This technique enables highest level of learner engagement in the training process.</p>
          </div>
        </div>
        <div style="display: flex; gap: 1rem; margin-bottom: 1rem;">
          <div style="flex-shrink: 0; width: 36px; height: 36px; background: #f0fdf9; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #0d9488; font-size: 1rem;">👥</div>
          <div>
            <p><strong>Renowned Trainers:</strong> Learn from the best and the most experienced instructors in the industry with decades of combined experience.</p>
          </div>
        </div>
        <div style="display: flex; gap: 1rem; margin-bottom: 1rem;">
          <div style="flex-shrink: 0; width: 36px; height: 36px; background: #fef3c7; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #d97706; font-size: 1rem;">👨‍🏫</div>
          <div>
            <p><strong>Personalized Coaching:</strong> Our curriculum is designed to suit different types of learners and their individual requirements. Our instructors are invested in developing each student's skills to help them reach their potential.</p>
          </div>
        </div>
        <div style="display: flex; gap: 1rem; margin-bottom: 1rem;">
          <div style="flex-shrink: 0; width: 36px; height: 36px; background: #eff6ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #2563eb; font-size: 1rem;">📚</div>
          <div>
            <p><strong>Relevant Training Content:</strong> The content we present is current and applicable as per the current industry standards.</p>
          </div>
        </div>
        <div style="display: flex; gap: 1rem;">
          <div style="flex-shrink: 0; width: 36px; height: 36px; background: #fef2f2; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #dc2626; font-size: 1rem;">📊</div>
          <div>
            <p><strong>Proven Track Record:</strong> 4000+ candidates trained with 98% certification pass rate.</p>
          </div>
        </div>
      `,
      icon: <FaStar />,
      iconColor: "#d97706"
    },
    {
      id: 6,
      category: "training",
      question: "What is the Duration of Training?",
      answer: `
        <p><strong>Depending on the training module chosen,</strong> duration of training may vary between <strong>80 to 100 hours</strong> of theory and <strong>20 to 30 hours</strong> of practice modules.</p>
      `,
      icon: <FaClock />,
      iconColor: "#d97706"
    },
    {
      id: 7,
      category: "training",
      question: "What does the course fee include?",
      answer: `
        <p><strong>Course fee includes all material</strong> applicable during the training period along with online login for practice exercises.</p>
        <ul>
          <li>All study materials and reference guides</li>
          <li>Access to online practice exercises</li>
          <li>Mock tests and evaluations</li>
          <li>Certificate of completion</li>
        </ul>
      `,
      icon: <FaDollarSign />,
      iconColor: "#d97706"
    },
    {
      id: 8,
      category: "training",
      question: "Do you provide any reference material for training?",
      answer: `
        <p><strong>Yes.</strong> All the material required to complete the training would be provided to every student.</p>
        <p>This includes:</p>
        <ul>
          <li>Digital study materials</li>
          <li>Practice exercises</li>
          <li>Coding reference guides</li>
          <li>Access to coding resources</li>
        </ul>
      `,
      icon: <FaBook />,
      iconColor: "#d97706"
    },
    {
      id: 25,
      category: "training",
      question: "Does your training include practical scenarios for coding?",
      answer: `
        <p>All our coding training courses are embedded with practical coding training. We train and help entry-level coders gain the experience needed through a unique mentoring and training program. These programs were developed and run by our instructors who have worked with several MNCs.</p>
        <p>In addition to our traditional courses, we also provide hands-on practice exposure to coding for candidates who are seeking only practical training on a specific specialty.</p>
      `,
      icon: <FaLaptop />,
      iconColor: "#d97706"
    },
    {
      id: 33,
      category: "training",
      question: "Do you provide any crash courses?",
      answer: `
        <p>Yes. We provide crash courses for experienced candidates or for specialty training. We highly recommend and encourage all candidates to go through the formal training hours as every subject requires a minimum period of time to assimilate the knowledge.</p>
      `,
      icon: <FaBolt />,
      iconColor: "#d97706"
    },
    {
      id: 34,
      category: "training",
      question: "Do you provide specialty training?",
      answer: `
        <p>Yes. We provide specialty training to candidates who aspire to explore new avenues in medical coding to enhance their career to the next level. Some of the specialty coding settings we train include Emergency Department Coding, Surgery Coding, Inpatient DRG Coding, Interventional Radiology Coding, Urgent Care Coding, HCC Coding.</p>
      `,
      icon: <FaStethoscope />,
      iconColor: "#d97706"
    },
    {
      id: 39,
      category: "training",
      question: "Can I attend a demo class and then decide?",
      answer: `
        <p>Yes. You can attend a demo class initially for 2 to 3 days and then decide.</p>
      `,
      icon: <FaChalkboard />,
      iconColor: "#d97706"
    },
    {
      id: 42,
      category: "training",
      question: "What is the difference in classroom and online training?",
      answer: `
        <p>Mode of communication is the only basic difference in classroom and online training. We provide interactive instructor-led online training using high-end technology solutions, and ensure that the online sessions are similar to the classroom sessions.</p>
      `,
      icon: <FaLaptopHouse />,
      iconColor: "#d97706"
    },
    {
      id: 12,
      category: "certification",
      question: "What Certifications do you train for?",
      answer: `
        <p>We train students for various certifications from both AHIMA and AAPC, as listed below:</p>
        <ul>
          <li><strong>CCS (Certified Coding Specialist)</strong> from AHIMA</li>
          <li><strong>CDIP (Certified Documentation Improvement Practitioner)</strong> from AHIMA</li>
          <li><strong>CCS-P (Certified Coding Specialist-Physician)</strong> from AHIMA</li>
          <li><strong>CCA (Certified Coding Associate)</strong> from AHIMA</li>
          <li><strong>CPC (Certified Professional Coder)</strong> from AAPC</li>
          <li><strong>CPMA (Certified Professional Medical Auditor)</strong> from AAPC</li>
          <li><strong>CRC (Certified Risk Adjustment Coder)</strong> from AAPC</li>
        </ul>
      `,
      icon: <FaCertificate />,
      iconColor: "#2563eb"
    },
    {
      id: 13,
      category: "certification",
      question: "Do you provide exam support?",
      answer: `
        <p>We provide training based on a practical approach which enables every student to appear for all competitive certification programs. However, we also provide specific exam-oriented modules for students appearing for certification exams.</p>
      `,
      icon: <FaAward />,
      iconColor: "#2563eb"
    },
    {
      id: 14,
      category: "certification",
      question: "Do you provide exam passing guarantee?",
      answer: `
        <p>Passing certification examinations is based on student's punctuality and dedication towards learning the subject. We equip every student with the required knowledge and exposure to the exam simulations, thereby improving chances of passing a certification exam. However, we do not provide exam pass guarantee. Our track record so far is a minimum of 95% pass rate.</p>
      `,
      icon: <FaCheckCircle />,
      iconColor: "#2563eb"
    },
    {
      id: 15,
      category: "certification",
      question: "Do you provide membership support?",
      answer: `
        <p>We guide and provide support to candidates in getting membership from both AAPC and AHIMA.</p>
      `,
      icon: <FaUsers />,
      iconColor: "#2563eb"
    },
    {
      id: 26,
      category: "certification",
      question: "Why do I need to get certified?",
      answer: `
        <p>Whether you have just started your healthcare career journey or you are already participating in the race, getting certified will increase your employability and also enhance your success in your chosen profession.</p>
        <p>Certification provides the essential confidence to perform the job well. It also opens up more opportunities to advance your career to the next level. Getting certified from AAPC or AHIMA demonstrates your commitment to the healthcare industry and also demonstrates your proficiency in the subject.</p>
      `,
      icon: <FaChartLine />,
      iconColor: "#2563eb"
    },
    {
      id: 27,
      category: "certification",
      question: "Which certification is good for me?",
      answer: `
        <p>There are a variety of certifications provided by AHIMA and AAPC for demonstrating coding expertise. Whether you have decided to start an interesting career as a medical coder or are an experienced professional needing guidance, our instructors are available to provide you the right guidance to choose the appropriate certification course for you.</p>
      `,
      icon: <FaUserGraduate />,
      iconColor: "#2563eb"
    },
    {
      id: 16,
      category: "career",
      question: "Do you provide Job/Placement guarantee?",
      answer: `
        <p>Learn Thru Us Training Academy does not provide job guarantee. We provide assistance to students by providing information about job openings and in preparing for interviews. We do not charge or collect fees for placement support. There is no 100% guarantee for getting a job. We provide reference of job vacancies and company details.</p>
      `,
      icon: <FaBriefcase />,
      iconColor: "#dc2626"
    },
    {
      id: 28,
      category: "career",
      question: "Can I get a job abroad?",
      answer: `
        <p>Certified skilled medical coding professionals are always in demand across United States and also in Middle East. People with right knowledge and skill sets are immediately absorbed by companies across the world.</p>
      `,
      icon: <FaGlobe />,
      iconColor: "#dc2626"
    },
    {
      id: 29,
      category: "career",
      question: "Will my career be stable?",
      answer: `
        <p>This is the only recession-proof industry as "no one on earth is free of diseases". As long as there are patients in the hospital, this career is long-lasting. No one can guarantee 100% stable career in today's economy. However, certification and appropriate skills will always increase your opportunities for earning.</p>
      `,
      icon: <FaChartLine />,
      iconColor: "#dc2626"
    },
    {
      id: 30,
      category: "career",
      question: "Which companies hire for medical coding?",
      answer: `
        <p>Companies which actively process claims for US healthcare insurances and also work on behalf of physicians and hospitals hire for medical coding. There are many companies in India which include:</p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; margin: 0.8rem 0;">
          <ul style="margin: 0;">
            <li>Omega Healthcare</li>
            <li>e4e</li>
            <li>Deloitte</li>
            <li>Dell</li>
            <li>Cognizant Technologies</li>
            <li>Vee Technologies</li>
            <li>United Health Group</li>
            <li>GeBBS</li>
          </ul>
          <ul style="margin: 0;">
            <li>PrimEra</li>
            <li>R1</li>
            <li>Elico</li>
            <li>Medico</li>
            <li>Mmodal</li>
            <li>Anthelio</li>
            <li>PhyCare</li>
            <li>Pi International</li>
            <li>Visionary RCM</li>
            <li>Navigant, AGS, Ajuba, HGS and many more</li>
          </ul>
        </div>
      `,
      icon: <FaBuilding />,
      iconColor: "#dc2626"
    },
    {
      id: 31,
      category: "career",
      question: "Can I work from home for this job?",
      answer: `
        <p>If you are living in the US and have a valid work permit, it is possible to work from home for coding. But if you are residing in India, work from home opportunities are very limited for Medical Coding.</p>
      `,
      icon: <FaHome />,
      iconColor: "#dc2626"
    },
    {
      id: 35,
      category: "career",
      question: "Do you provide experience certificate?",
      answer: `
        <p><strong>NO, WE DO NOT PROVIDE ANY EXPERIENCE CERTIFICATE IF THE CANDIDATE HAS NOT WORKED WITH US.</strong></p>
      `,
      icon: <FaIdCard />,
      iconColor: "#dc2626"
    },
    {
      id: 36,
      category: "career",
      question: "Do I have to sign a bond to get a job in medical coding?",
      answer: `
        <p>Signing a bond may not be necessary for every organization in this industry, but this gives you an opportunity to prove yourself and also have employment guarantee.</p>
      `,
      icon: <FaFileSignature />,
      iconColor: "#dc2626"
    },
    {
      id: 24,
      category: "career",
      question: "Do you provide any internship?",
      answer: `
        <p>Internship is provided on a merit basis to all students upon completion of the training program. Every candidate should successfully complete all the training modules and achieve a minimum baseline percentage to be eligible for internship. Certification of internship is provided upon successful completion.</p>
      `,
      icon: <FaUserGraduate />,
      iconColor: "#dc2626"
    },
    {
      id: 38,
      category: "logistics",
      question: "When will the next batch start?",
      answer: `
        <p>We start a new batch every 15 days which helps us accommodate every student in a flexible way. Please contact us for further details.</p>
        <div style="background: #f8f7fc; border-radius: 12px; padding: 1.2rem; margin-top: 1.2rem;">
          <h4 style="color: #1a1a2e; margin-bottom: 0.8rem; font-size: 1rem; font-family: 'Times New Roman', serif;">Contact us for batch details:</h4>
          <div style="display: flex; align-items: center; gap: 0.8rem; margin-bottom: 0.6rem;">
            <FaPhone style="color: #3730A3;" />
            <span>+1 (888) 123-4567</span>
          </div>
          <div style="display: flex; align-items: center; gap: 0.8rem;">
            <FaEnvelope style="color: #3730A3;" />
            <span>info@learnthruus.com</span>
          </div>
        </div>
      `,
      icon: <FaCalendarWeek />,
      iconColor: "#059669"
    },
    {
      id: 40,
      category: "logistics",
      question: "What is the best time to reach you for any queries?",
      answer: `
        <p>We are available <strong>8 AM to 10 PM IST</strong> to answer your queries and guide you for the appropriate course.</p>
      `,
      icon: <FaRegClock />,
      iconColor: "#059669"
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Got Questions?
          <span>We've Got Answers</span>
        </HeroTitle>
        <HeroDescription>
          Find answers to common questions about medical coding training, eligibility, 
          career opportunities, certifications, and more.
        </HeroDescription>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search your question..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
      </HeroSection>

      <CategoriesContainer>
        <CategoriesGrid>
          {categories.map((category) => (
            <CategoryButton
              key={category.id}
              active={activeCategory === category.id}
              color={category.color}
              onClick={() => setActiveCategory(category.id)}
            >
              <CategoryIcon>{category.icon}</CategoryIcon>
              <CategoryName>{category.name}</CategoryName>
            </CategoryButton>
          ))}
        </CategoriesGrid>
      </CategoriesContainer>

      <FAQContainer>
        {filteredFaqs.length === 0 ? (
          <NoResults>
            <FaQuestionCircle />
            <h3>No questions found</h3>
            <p>Try adjusting your search or filter</p>
          </NoResults>
        ) : (
          filteredFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FAQQuestion onClick={() => toggleItem(faq.id)}>
                <QuestionContent>
                  <QuestionIcon bg={`${faq.iconColor}15`} iconColor={faq.iconColor}>
                    {faq.icon}
                  </QuestionIcon>
                  <QuestionText>{faq.question}</QuestionText>
                </QuestionContent>
                <ChevronIcon open={openItems[faq.id]}>
                  <FaChevronDown />
                </ChevronIcon>
              </FAQQuestion>
              
              <AnimatePresence>
                {openItems[faq.id] && (
                  <FAQAnswer
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </FAQAnswer>
                )}
              </AnimatePresence>
            </FAQItem>
          ))
        )}
      </FAQContainer>

      <StillHaveQuestions>
        <StillTitle>Still Have Questions?</StillTitle>
        <StillDescription>
          Can't find the answer you're looking for? Please reach out to our friendly team.
        </StillDescription>
        <ContactButtons>
          <ContactButton href="mailto:info@learnthruus.com" className="primary">
            <FaEnvelope /> Email Us
          </ContactButton>
          <ContactButton href="tel:+18881234567" className="secondary">
            <FaPhone /> Call Us
          </ContactButton>
        </ContactButtons>
      </StillHaveQuestions>
    </PageContainer>
  );
};

export default FAQ;